import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import {
  assignExperimentValue,
  getExperiment,
  initializeStatsig,
  trackPerformance,
  trackPerformanceAppInitiated,
  trackPerformancePageLoaded,
} from '@libs/feature-toggle';
import { getLocale } from '@libs/sembo-dates';
import { decodeHTML, measureInitializationPerformance } from '@libs/utils';

import App from './App.vue';
import { languageLocale } from './modules/localization/localeUtils';
import { createLogger } from './modules/plugins/logger';
import './styles/main.scss';

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

const initializationPerformanceMeasurement = measureInitializationPerformance();
trackPerformance();

(async function (): Promise<void> {
  await initializeStatsig();

  initializationPerformanceMeasurement.then(
    ({ timeToAppInitiatedMs, timeToPageLoadedMs }) => {
      trackPerformancePageLoaded(timeToPageLoadedMs);
      trackPerformanceAppInitiated(timeToAppInitiatedMs);
    }
  );

  // NOTE: can be removed when other experiments available
  const exampleExperiment = getExperiment('example_experiment').get(
    'should_show_example_experiment',
    false
  );
  assignExperimentValue('example_experiment', exampleExperiment);
})();

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

// NOTE: This loads sembo shared web components after app is mounted,
// to not delay first request to smart search.
window.addEventListener('load', () => {
  const scripts = [
    'https://sembo-inspire-apis.sembo.travel/share-component/latest/main.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-auth/latest/sembo-auth.js',
  ];

  scripts.forEach((src) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  });
});

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale);
